import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "../../axios_config";
import { BASE_API_URL } from "../../common/endpoints";
import { useUserDataContext } from "../../user_data_context";
import { useSearchParams } from "react-router-dom";

const BiblePageContext = createContext();

export const useBiblePageState = () => {
  const { userData } = useUserDataContext();
  const [searchParams] = useSearchParams();
  const bibleId = searchParams.get("bibleId");
  const bookId = searchParams.get("bookId");
  const chapterId = searchParams.get("chapterId");

  const [currentTranslationData, setCurrentTranslationData] = useState([]);
  const [showStrongsCode, setShowStrongsCode] = useState(false);
  const [strongsCodeModalData, setStrongsCodeModalData] = useState(null);
  const [currentTestament, setCurrentTestament] = useState("");
  const [activeScreenMobile, setActiveScreenMobile] = useState("bible_chapter"); // bible_chapter, bible_navigation, bible_translation, bible_compare, bible_search
  const [bibleHistory, setBibleHistory] = useState([]);
  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);

  const maxHistoryLength = 10;

  const fetchBibleHistory = async () => {
    if (!userData || !userData._id) return;

    try {
      const response = await axios.get(`${BASE_API_URL}/users/${userData._id}`);
      setBibleHistory(response.data?.user_bible_history || []);
      setIsHistoryLoaded(true);
    } catch (error) {
      console.error("Error fetching bible history:", error);
    }
  };

  const updateBibleHistory = async (newHistory) => {
    if (!userData || !userData._id) return;

    try {
      await axios.patch(`${BASE_API_URL}/users/${userData._id}`, {
        user_bible_history: newHistory,
      });
      setBibleHistory(newHistory);
    } catch (error) {
      console.error("Error updating bible history:", error);
    }
  };

  useEffect(() => {
    if (isHistoryLoaded && bibleId && bookId && chapterId) {
      const ch = parseInt(chapterId, 10);

      if (!isNaN(ch)) {
        const newEntry = { b: bibleId, bk: bookId, ch };

        const filteredHistory = bibleHistory.filter(
          (item) =>
            !(item.b === bibleId && item.bk === bookId && item.ch === ch)
        );

        const updatedHistory = [newEntry, ...filteredHistory].slice(
          0,
          maxHistoryLength
        );

        updateBibleHistory(updatedHistory);
      }
    }
  }, [isHistoryLoaded, bibleId, bookId, chapterId]);

  useEffect(() => {
    if (userData && userData._id) {
      fetchBibleHistory();
    }
  }, [userData]);

  return {
    currentTranslationData,
    setCurrentTranslationData,
    showStrongsCode,
    setShowStrongsCode,
    strongsCodeModalData,
    setStrongsCodeModalData,
    currentTestament,
    setCurrentTestament,
    activeScreenMobile,
    setActiveScreenMobile,
    bibleHistory,
    setBibleHistory,
  };
};

export const BiblePageProvider = ({ children }) => {
  const {
    currentTranslationData,
    setCurrentTranslationData,
    showStrongsCode,
    setShowStrongsCode,
    strongsCodeModalData,
    setStrongsCodeModalData,
    currentTestament,
    setCurrentTestament,
    activeScreenMobile,
    setActiveScreenMobile,
    bibleHistory,
    setBibleHistory,
  } = useBiblePageState();

  return (
    <BiblePageContext.Provider
      value={{
        currentTranslationData,
        setCurrentTranslationData,
        showStrongsCode,
        setShowStrongsCode,
        strongsCodeModalData,
        setStrongsCodeModalData,
        currentTestament,
        setCurrentTestament,
        activeScreenMobile,
        setActiveScreenMobile,
        bibleHistory,
        setBibleHistory,
      }}
    >
      {children}
    </BiblePageContext.Provider>
  );
};

export const useBiblePageContext = () => {
  return useContext(BiblePageContext);
};
